import { useState } from "react";
import { BellRing, Copy, Download, Plus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { calcTotals, fmtDate, money } from "@/lib/money";
import { Btn, Field, StatusPill, inputCls } from "@/components/admin/ui";

const LineItems = ({ items, setItems, locked }) => {
  const upd = (i, k, v) => setItems(items.map((li, j) => (j === i ? { ...li, [k]: v } : li)));
  return (
    <div className="space-y-2" data-testid="invoice-line-items">
      {items.map((li, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <input disabled={locked} placeholder="Description" value={li.description} onChange={(e) => upd(i, "description", e.target.value)} className={`${inputCls} col-span-12 sm:col-span-6`} data-testid={`line-desc-${i}`} />
          <input disabled={locked} type="number" min="0" step="any" placeholder="Qty" value={li.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className={`${inputCls} col-span-3 sm:col-span-2`} data-testid={`line-qty-${i}`} />
          <input disabled={locked} type="number" min="0" step="0.01" placeholder="Price" value={li.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className={`${inputCls} col-span-5 sm:col-span-2`} data-testid={`line-price-${i}`} />
          <span className="col-span-3 self-center text-right text-sm text-zinc-300 sm:col-span-1">{money((Number(li.quantity) || 0) * (Number(li.unit_price) || 0))}</span>
          {!locked && items.length > 1 && <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="col-span-1 self-center text-zinc-400 hover:text-red-400" aria-label="Remove line" data-testid={`line-remove-${i}`}><X className="h-4 w-4" /></button>}
        </div>
      ))}
      {!locked && <Btn onClick={() => setItems([...items, { description: "", quantity: 1, unit_price: 0 }])} data-testid="line-add-btn"><Plus className="h-4 w-4" /> Add line</Btn>}
    </div>
  );
};

export const InvoiceEditor = ({ initial, clients, onSaved }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const locked = ["paid", "void"].includes(f.status);
  const { total, deposit } = calcTotals(f.line_items, f.deposit_type, f.deposit_value);
  const link = f.token ? `${window.location.origin}/i/${f.token}` : "";
  const valid = f.client_id && f.title && f.line_items.every((li) => li.description && Number(li.quantity) > 0);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        client_id: f.client_id, title: f.title, due_date: f.due_date || "", notes: f.notes || "",
        deposit_type: f.deposit_type, deposit_value: Number(f.deposit_value) || 0, reminders_enabled: f.reminders_enabled !== false, event_date: f.event_date || "",
        line_items: f.line_items.map((li) => ({ description: li.description, quantity: Number(li.quantity), unit_price: Number(li.unit_price) || 0 })),
      };
      const { data } = f.id ? await api.put(`/admin/invoices/${f.id}`, body) : await api.post("/admin/invoices", body);
      setF(data);
      onSaved(data);
      toast.success("Invoice saved");
      return data;
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/invoices/${f.id}/remind`);
      const next = { ...f, reminder_log: data?.reminder_log ?? f.reminder_log };
      setF(next);
      onSaved(next);
      toast.success(`Reminder emailed to ${f.client_email}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const send = async () => {
    const saved = await save();
    if (!saved) return;
    setBusy(true);
    try {
      await api.post(`/admin/invoices/${saved.id}/send`, { origin_url: window.location.origin });
      const next = { ...saved, status: saved.status === "draft" ? "sent" : saved.status };
      setF(next);
      onSaved(next);
      toast.success(`Invoice emailed to ${saved.client_email}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="invoice-editor">
      {f.id && <div className="flex items-center gap-3"><span className="font-mono-rc text-xs text-zinc-400">{f.number}</span><StatusPill status={f.status} /></div>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Client">
          <select disabled={locked} value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className={inputCls} data-testid="invoice-client-select">
            <option value="">Select a client</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.organization ? ` — ${c.organization}` : ""}</option>)}
          </select>
        </Field>
        <Field label="Due date"><input disabled={locked} type="date" value={f.due_date || ""} onChange={(e) => setF({ ...f, due_date: e.target.value })} className={inputCls} data-testid="invoice-due-input" /></Field>
      </div>
      <Field label="Invoice title"><input disabled={locked} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Album mixing — 4 songs" className={inputCls} data-testid="invoice-title-input" /></Field>
      <Field label="Event / session date (optional — blacked out on your calendar once the deposit is paid)"><input disabled={locked} type="date" value={f.event_date || ""} onChange={(e) => setF({ ...f, event_date: e.target.value })} className={inputCls} data-testid="invoice-event-date-input" /></Field>
      <Field label="Line items (description · qty · price)"><LineItems items={f.line_items} setItems={(line_items) => setF({ ...f, line_items })} locked={locked} /></Field>
      <div className="grid grid-cols-1 gap-4 rounded-2xl border border-[#2E2A24] p-4 sm:grid-cols-3">
        <Field label="Deposit">
          <select disabled={locked || f.amount_paid > 0} value={f.deposit_type} onChange={(e) => setF({ ...f, deposit_type: e.target.value })} className={inputCls} data-testid="invoice-deposit-type">
            <option value="none">No deposit — pay in full</option>
            <option value="percent">Percentage of total</option>
            <option value="fixed">Fixed amount</option>
          </select>
        </Field>
        {f.deposit_type !== "none" && (
          <Field label={f.deposit_type === "percent" ? "Deposit %" : "Deposit $"}>
            <input disabled={locked || f.amount_paid > 0} type="number" min="0" step="0.01" value={f.deposit_value} onChange={(e) => setF({ ...f, deposit_value: e.target.value })} className={inputCls} data-testid="invoice-deposit-value" />
          </Field>
        )}
        <div className="self-end text-right text-sm sm:col-start-3">
          <p className="text-zinc-400">Total <span className="ml-2 font-display text-2xl text-[#F5EFE6]" data-testid="invoice-total">{money(total)}</span></p>
          {f.deposit_type !== "none" && <p className="text-zinc-400">Deposit <span className="ml-2 text-[#E09838]" data-testid="invoice-deposit-amount">{money(deposit)}</span></p>}
          {f.amount_paid > 0 && <p className="text-emerald-300">Paid {money(f.amount_paid)}</p>}
        </div>
      </div>
      <Field label="Notes for the client (optional)"><textarea disabled={locked} rows={3} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} data-testid="invoice-notes-input" /></Field>
      <label className="flex items-start gap-3 text-sm text-zinc-300">
        <input type="checkbox" disabled={locked} checked={f.reminders_enabled !== false} onChange={(e) => setF({ ...f, reminders_enabled: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#C4832B]" data-testid="invoice-reminders-checkbox" />
        <span>Send automatic payment reminders <span className="block text-xs text-zinc-400">Friendly emails 3 days before the due date, and 3 and 10 days after, until it's paid. Needs a due date.</span></span>
      </label>
      {f.reminder_log?.length > 0 && <p className="text-xs text-zinc-400" data-testid="invoice-reminder-log">Reminders sent: {f.reminder_log.map((x) => fmtDate(x.sent_at)).join(", ")}</p>}
      {f.payments?.length > 0 && (
        <div className="rounded-xl border border-[#2E2A24] p-4 text-sm" data-testid="invoice-payments">
          {f.payments.map((p, i) => <p key={i} className="text-zinc-300">{money(p.amount)} · {p.kind} · {fmtDate(p.paid_at)}</p>)}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {f.amount_paid > 0 && f.token && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/invoices/${f.token}/pdf`} className="inline-flex items-center gap-2 rounded-full border border-[#2E2A24] px-4 py-2 text-sm hover:border-[#C4832B]" data-testid="invoice-admin-pdf"><Download className="h-4 w-4" /> Download PDF</a>
        )}
        {link && !locked && f.status !== "draft" && (
          <Btn onClick={remind} disabled={busy} data-testid="invoice-remind-btn"><BellRing className="h-4 w-4" /> Send reminder</Btn>
        )}
        {link && f.status !== "draft" && <Btn onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="invoice-copy-link"><Copy className="h-4 w-4" /> Copy client link</Btn>}
        {!locked && <Btn onClick={save} disabled={busy || !valid} data-testid="invoice-save-btn">Save draft</Btn>}
        {!locked && <Btn variant="primary" onClick={send} disabled={busy || !valid || total <= 0} data-testid="invoice-send-btn"><Send className="h-4 w-4" /> {f.status === "draft" || !f.id ? "Save & send" : "Resend"}</Btn>}
      </div>
    </div>
  );
};
